/**
 * Lite Join API Client
 * 處理 /join 頁面的 API 調用
 */

import { getApiBase } from './apiUtils.js';
import { handleLiteErrorResponse } from './liteApiError.js';

/**
 * 活動資訊（包含商品清單）
 */
/** 自訂用戶回覆格式（用於 liff.sendMessages 操作回覆；Group 與 Square 共用） */
export interface UserReplyFormat {
  autoReplyEnabled?: boolean;
  prefix?: string;
  operationReply?: boolean;
  remainingBelowThreshold?: number | null;
  suffix?: string;
  /** 是否在回覆／分享內容中帶入活動報名 URL */
  includeActivityUrl?: boolean;
}

/** Join 頁面單一商品（含總量） */
export interface JoinProductSimple {
  id: string;
  name: string;
  price: number;
  quantity?: number;
  totalQuantity?: number;
}

/** Join 頁面多規格商品（共用總量） */
export interface JoinProductVariant {
  id: string;
  name: string;
  maxTotalQuantity: number;
  specs: Array<{ id: string; name: string; price: number }>;
  totalQuantity?: number;
}

export type JoinProduct = JoinProductSimple | JoinProductVariant;

export interface JoinActivity {
  id: string;
  groupId: string;
  title: string;
  description: string | null;
  createdBy: string | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  productList: JoinProduct[];
  remarks?: string | null;
  /** 當前使用者的暱稱（需登入，用於參與名單與修改表單） */
  displayName?: string | null;
  userReplyFormat?: UserReplyFormat | null;
  /** 活動報名短網址（/g/:shortCode），分享時一律使用 */
  shortUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * 用戶商品選擇
 */
export interface UserItem {
  id: string;
  userId: string;
  activityId: string;
  productKey: string;
  quantity: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * 取得活動資訊（用於 /join 頁面）
 * @param liteOptions.squareSuffix - Square 路徑時必傳，供後端解析 shop（與 Square 先前解法一致）
 */
export async function getJoinActivity(
  activityId: string,
  jwtToken?: string | null,
  liteOptions?: { squareSuffix?: string }
): Promise<JoinActivity> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  const response = await liteApiFetch(
    `${apiBase}/api/lite/join/${activityId}`,
    {
      method: 'GET',
      headers,
    },
    liteOptions?.squareSuffix?.trim()
      ? { squareSuffix: liteOptions.squareSuffix.trim() }
      : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '取得活動資訊失敗，請稍後再試',
    });
  }

  const result = await response.json();
  console.log('✅ 取得活動資訊成功:', result);
  return result.data;
}

/** 後端回傳的補正通知觸發資訊（供 LIFF 送出一則觸發訊息，讓 webhook 以 Reply @mention 通知被補正者） */
export interface WaitlistNotifyPayload {
  groupId: string;
  activityId: string;
  productKey: string;
}

/** Join 頁 API 的 Lite 脈絡（與 getJoinActivity 一致；Square 時必傳 suffix 才通過後端驗證） */
export type JoinLiteOptions = { squareSuffix?: string };

/**
 * 更新用戶商品選擇（加減操作）
 * @param lineUserId - LIFF profile.userId；第一次登入時 table 無資料，傳此欄位由後端 find-or-create 寫入 lite.users
 * @param liteOptions - Square 路徑時必傳 squareSuffix，後端才能解析 shop（與 getJoinActivity 一致）
 * @returns 含 data 與選用的 waitlistNotify（有補正時存在，LIFF 需依此送出一則觸發訊息以在社團 Reply @mention）
 */
export async function updateJoinItem(
  activityId: string,
  userId: string,
  productKey: string,
  action: 'add' | 'remove',
  jwtToken?: string | null,
  lineUserId?: string | null,
  liteOptions?: JoinLiteOptions
): Promise<{ data: UserItem; waitlistNotify?: WaitlistNotifyPayload }> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (jwtToken) {
    headers['Authorization'] = `Bearer ${jwtToken}`;
  }

  const body: Record<string, unknown> = {
    userId,
    productKey,
    action,
  };
  if (lineUserId) body.lineUserId = lineUserId;

  const response = await liteApiFetch(
    `${apiBase}/api/lite/join/${activityId}/items`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    },
    liteOptions?.squareSuffix?.trim()
      ? { squareSuffix: liteOptions.squareSuffix.trim() }
      : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '更新商品選擇失敗，請稍後再試',
    });
  }

  const result = (await response.json()) as {
    data: UserItem;
    waitlistNotify?: WaitlistNotifyPayload;
  };
  console.log('✅ 更新商品選擇成功:', result);
  return { data: result.data, waitlistNotify: result.waitlistNotify };
}

/**
 * 取得用戶的商品選擇列表
 * @param liteOptions - Square 路徑時必傳 squareSuffix，後端才能解析 shop（與 getJoinActivity 一致）
 */
export async function getUserItems(
  userId: string,
  activityId: string,
  jwtToken: string,
  liteOptions?: JoinLiteOptions
): Promise<UserItem[]> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  const params = new URLSearchParams({
    userId,
    activityId,
  });

  const url = `${apiBase}/api/lite/orders/items?${params.toString()}`;
  console.log('📤 取得用戶商品選擇列表:', { userId, activityId, url });

  const response = await liteApiFetch(
    url,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
    },
    liteOptions?.squareSuffix?.trim()
      ? { squareSuffix: liteOptions.squareSuffix.trim() }
      : undefined
  );

  if (!response.ok) {
    let errorMessage = '取得商品選擇列表失敗，請稍後再試';
    try {
      const error = await response.json();
      console.error('❌ 取得商品選擇列表失敗:', error);
      errorMessage = error.error || error.message || errorMessage;
    } catch {
      console.error(
        '❌ 取得商品選擇列表失敗（無法解析錯誤）:',
        response.status,
        response.statusText
      );
      errorMessage = `取得商品選擇列表失敗 (${response.status}): ${response.statusText || '請稍後再試'}`;
    }
    throw new Error(errorMessage);
  }

  const result = await response.json();
  console.log('✅ 取得商品選擇列表成功:', result);
  return result.data;
}

/**
 * 更新參與備註
 * PATCH /api/lite/join/:activityId/remarks
 * @param liteOptions - Square 路徑時必傳 squareSuffix，後端才能解析 shop
 */
export async function updateJoinRemarks(
  activityId: string,
  remarks: string | null,
  jwtToken: string,
  liteOptions?: JoinLiteOptions
): Promise<void> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  const response = await liteApiFetch(
    `${apiBase}/api/lite/join/${activityId}/remarks`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ remarks }),
    },
    liteOptions?.squareSuffix?.trim()
      ? { squareSuffix: liteOptions.squareSuffix.trim() }
      : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '更新備註失敗，請稍後再試',
    });
  }
}

/**
 * 更新當前使用者的暱稱
 * PATCH /api/lite/join/:activityId/display-name
 * @param liteOptions - Square 路徑時必傳 squareSuffix，後端才能解析 shop
 */
export async function updateJoinDisplayName(
  activityId: string,
  displayName: string | null,
  jwtToken: string,
  liteOptions?: JoinLiteOptions
): Promise<void> {
  const apiBase = getApiBase();
  const { liteApiFetch } = await import('./apiUtils.js');

  const response = await liteApiFetch(
    `${apiBase}/api/lite/join/${activityId}/display-name`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ displayName }),
    },
    liteOptions?.squareSuffix?.trim()
      ? { squareSuffix: liteOptions.squareSuffix.trim() }
      : undefined
  );

  if (!response.ok) {
    await handleLiteErrorResponse(response, {
      defaultMessage: '更新暱稱失敗，請稍後再試',
    });
  }
}
